import { Route, Routes, useLocation } from "react-router-dom";

import Address from "./pages/Address";
import Age from "./pages/Age";
import { ContextProvider } from "./contexts/data";
import Email from "./pages/Email";
import Footer from "./components/Footer";
import GuildConfirmation from "./pages/GuildConfirm";
import Head from "./components/Head";
import HiddenInput from "./components/HiddenInput";
import HomeAmount from "./pages/HomeAmount";
import ManualAddress from "./pages/ManualAddress";
import NoticeAtCollection from "./pages/NoticeAtCollection";
import MortgageBalance from "./pages/MortgageBalance";
import PhoneNumber from "./pages/PhoneNumber";
import RequireAge from "./components/RequireAge";
import Result from "./pages/Result";
import TestResult from "./pages/TestResult";
import UserName from "./pages/UserName";
import Verification from "./pages/Verification";
import ZipCode from "./pages/ZipCode";
import { getLenderByBuyer } from "./util/lenders";
import { handleQueryStrings } from "./util/querystring";
import styled from "styled-components";
import { useEffect } from "react";

const {
  ppcid,
  cid,
  tsid,
  gclid,
  offer,
  fullQueryString,
  phoneNumber,
  changeHeadline,
  isPurchase,
  isCobrand
} = handleQueryStrings();

// The lender named on /result-american-senior, resolved from the same table
// the in-app flow uses for a matched buyer. If the entry is ever removed the
// route degrades to the neutral forwarded page rather than naming anyone.
const AMERICAN_SENIOR_LENDER = getLenderByBuyer("HighTechLending-reverse");

function App() {
  const location = useLocation();

  useEffect(() => {
    if (window.innerWidth < 500) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, [location]);

  return (
    <ContextProvider ppcid={ppcid} cid={cid} gclid={gclid} tsid={tsid} fullQueryString={fullQueryString}>
      <form style={{ display: "none" }}></form>
      <OutWrapper>
        <Head phoneNumber={phoneNumber} isCobrand={isCobrand} />
        <Wrapper>
          <Routes>
            <Route
              path="/"
              element={
                <Age
                  changeHeadline={changeHeadline} isPurchase={isPurchase}
                />
              }
            />
            {/* Every data-collecting step (and the pre-submit Guild page) is
                wrapped in RequireAge so an evicted-webview reload cannot
                finish the funnel age-less. Left unwrapped: /result (display
                only — bouncing an already-submitted session would invite a
                duplicate redo), /result-problem, /result-american-senior,
                /test-result and /notice-at-collection (standalone pages
                meant to be opened directly). */}
            <Route path="/address" element={<RequireAge><Address /></RequireAge>} />
            <Route path="/email" element={<RequireAge><Email /></RequireAge>} />
            <Route path="/guild-confirmation" element={<RequireAge><GuildConfirmation /></RequireAge>} />
            <Route path="/verification" element={<RequireAge><Verification /></RequireAge>} />
            <Route path="/home-value" element={<RequireAge allowAgeZipParams><HomeAmount /></RequireAge>} />
            <Route path="/manual-address" element={<RequireAge><ManualAddress /></RequireAge>} />
            <Route
              path="/mortgage-balance"
              element={<RequireAge><MortgageBalance /></RequireAge>}
            />
            <Route
              path="/phone-number"
              element={<RequireAge><PhoneNumber /></RequireAge>}
            />
            <Route path="/result" element={<Result />} />
            {/* Outcome pages for a partner-hosted form that posts to our API
                and then redirects the consumer to us. Our response carries a
                lender_redirect_url when the matched buyer has an approved
                call-out -> that URL (/result-american-senior renders the same
                American Senior Lending content the in-app flow shows for
                that buyer); otherwise Matched -> /result (the neutral
                forwarded page on a cold load); anything else ->
                /result-problem (the same submit-problem message the in-app
                flow shows). All display only, no lead post. */}
            <Route path="/result-problem" element={<Result forceSubmitProblem />} />
            <Route
              path="/result-american-senior"
              element={<Result forceMatchedLender={AMERICAN_SENIOR_LENDER} />}
            />
            <Route path="/test-result" element={<TestResult />} />
            <Route
              path="/zip-code"
              element={<RequireAge><ZipCode offer={offer} /></RequireAge>}
            />
            <Route path="/user-name" element={<RequireAge><UserName /></RequireAge>} />
            <Route path="/notice-at-collection" element={<NoticeAtCollection />} />
            <Route
              path="*"
              element={
                <Age
                  changeHeadline={changeHeadline} isPurchase={isPurchase}
                />
              }
            />
          </Routes>
          <HiddenInput isShow={location.pathname === "/phone-number" || location.pathname === '/verification'} />
        </Wrapper>
        <Footer
          hideBBB={
            location.pathname === "/result"
          }
        />
      </OutWrapper>
    </ContextProvider>
  );
}

const OutWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const Wrapper = styled.div`
  width: 100%;
  flex: 1;
  margin-top: 40px;
  display: flex;
  justify-content: "center";
  align-items: center;
  flex-direction: column;

  > div {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .fade-enter {
    opacity: 0;
    transform: translate(0, 100%);
    z-index: 1;
  }
  .fade-enter.fade-enter-active {
    opacity: 1;
    transform: translate(0, 0);
    transition: opacity 1s ease-out, transform 1s ease;
  }
  .fade-exit {
    opacity: 1;
    transform: translate(0, 0);
  }
  .fade-exit.fade-exit-active {
    opacity: 0;
    transform: translate(0, 100%);
    transition: opacity 1s ease-out, transform 1s ease;
  }
`;

export default App;
